import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { messagesApi } from "@/api/messaging";
import type { MessageAttachmentDto, UserPickerDto } from "@/api/messaging";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/design-system/lib/utils";

/**
 * Internal Messaging v1.0 §6 — "New message" picker. Search-as-you-type
 * (debounced 250ms), max 20 results, excludes self. Selecting a user
 * navigates to /messages/new?to={id}, where the empty thread renders and
 * the conversation is created on first send.
 *
 * Internal Messaging Integration Standard v1.1 §5 — when invoked from an
 * entity Messages tab, callers pass <c>prefillAttachment</c> so the new
 * thread opens with that record already chip-attached to the draft.
 */
export interface NewMessageModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optimistic chip to seed the new thread's draft. Forwarded to the
   * empty-thread route via React Router state so the URL stays clean.
   */
  prefillAttachment?: MessageAttachmentDto;
}

export function NewMessageModal({
  open,
  onClose,
  prefillAttachment,
}: NewMessageModalProps) {
  const navigate = useNavigate();
  const [raw, setRaw] = useState("");
  const [q, setQ] = useState("");

  // 250ms debounce as specified.
  useEffect(() => {
    const handle = window.setTimeout(() => setQ(raw.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [raw]);

  // Reset on close so the next open is clean.
  useEffect(() => {
    if (!open) {
      setRaw("");
      setQ("");
    }
  }, [open]);

  const users = useQuery({
    queryKey: ["messaging", "user-picker", q],
    queryFn: () => messagesApi.searchUsers(q),
    enabled: open,
    staleTime: 15_000,
  });

  const empty = useMemo(
    () => !users.isLoading && (users.data?.length ?? 0) === 0,
    [users.isLoading, users.data?.length],
  );

  return (
    <Modal open={open} onClose={onClose} dismissOnBackdropClick={false} title="New message" size="md">
      <div className="relative">
        <Search
          size={16}
          strokeWidth={1.5}
          className="absolute left-space-3 top-1/2 -translate-y-1/2 text-gray-mid"
          aria-hidden
        />
        <input
          autoFocus
          type="search"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="Search by name or email…"
          aria-label="Search users"
          className={cn(
            "w-full h-10 pl-10 pr-space-3 rounded-md font-body text-small",
            "bg-white-pure text-onyx border border-border-light",
            "focus:outline-none focus:border-gold focus:shadow-goldRing",
          )}
        />
      </div>

      <ul className="mt-space-3 m-0 p-0 list-none max-h-[50vh] overflow-y-auto">
        {users.isLoading && (
          <li className="text-small text-gray-mid p-space-3">Searching…</li>
        )}
        {empty && (
          <li className="text-small text-gray-mid p-space-3">
            {q ? `No users matching "${q}".` : "Start typing to find a teammate."}
          </li>
        )}
        {users.data?.map((u: UserPickerDto) => (
          <li key={u.id}>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/messages/new?to=${encodeURIComponent(u.id)}`, {
                  state: prefillAttachment
                    ? { prefillAttachment }
                    : undefined,
                });
              }}
              className={cn(
                "w-full flex items-center gap-space-3 px-space-3 py-space-2 rounded-md",
                "hover:bg-white-warm focus-visible:outline-none focus-visible:bg-white-warm text-left",
              )}
            >
              <UserAvatar name={u.displayName} avatarUrl={u.avatarUrl} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-small text-onyx truncate">
                  {u.displayName}
                </div>
                <div className="text-label text-gray-mid truncate">
                  {u.email}
                  {u.primaryRoleCode && ` • ${u.primaryRoleCode}`}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

function UserAvatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="h-8 w-8 rounded-pill object-cover" />;
  }
  const initials = name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span className="h-8 w-8 rounded-pill bg-gold text-onyx flex items-center justify-center text-label font-display font-bold">
      {initials || "?"}
    </span>
  );
}
